// src/components/UI/components/DropdownMenu/DropdownMenuRadioItem.tsx

'use client';
import * as DropdownMenuPrimitives from '@radix-ui/react-dropdown-menu';
import { forwardRef } from 'react';
import { cx } from '../../lib/utils';
import {
  RiCheckboxBlankCircleLine,
  RiCheckLine,
  RiRadioButtonFill,
} from '@remixicon/react';

const DropdownMenuRadioItem = forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitives.RadioItem>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitives.RadioItem> & {
    shortcut?: string;
    hint?: string;
    iconType?: 'check' | 'radio';
  }
>(
  (
    { className, hint, shortcut, children, iconType = 'radio', ...props },
    forwardedRef,
  ) => (
    <DropdownMenuPrimitives.RadioItem
      ref={forwardedRef}
      className={cx(
        // base
        'group/DropdownMenuRadioItem relative flex cursor-pointer select-none items-center gap-x-2 rounded py-2 pl-8 pr-2 outline-none transition-colors font-semibold sm:text-sm',
        // text color
        'text-revving-content dark:text-darkRevving-content',
        // disabled
        'data-[disabled]:pointer-events-none data-[disabled]:text-gray-400 data-[disabled]:hover:bg-none dark:data-[disabled]:text-gray-600',
        // focus
        'focus-visible:text-revving-content-strong focus-visible:bg-revving-brand-faint focus-visible:dark:text-darkRevving-content-strong focus-visible:dark:bg-darkRevving-brand-faint',
        // hover
        'hover:text-revving-content-strong hover:bg-revving-brand-faint hover:dark:text-darkRevving-content-strong hover:dark:bg-darkRevving-brand-faint',
        className,
      )}
      {...props}
    >
      {iconType === 'radio' ? (
        <span className="absolute left-2 flex size-4 items-center justify-center">
          <RiRadioButtonFill
            aria-hidden="true"
            className="size-full shrink-0 text-blue-500 group-data-[state=checked]/DropdownMenuRadioItem:flex group-data-[state=unchecked]/DropdownMenuRadioItem:hidden dark:text-blue-500"
          />
          <RiCheckboxBlankCircleLine
            aria-hidden="true"
            className="size-full shrink-0 text-gray-300 group-data-[state=unchecked]/DropdownMenuRadioItem:flex group-data-[state=checked]/DropdownMenuRadioItem:hidden dark:text-gray-700"
          />
        </span>
      ) : iconType === 'check' ? (
        <span className="absolute left-2 flex size-4 items-center justify-center">
          <RiCheckLine
            aria-hidden="true"
            className="size-full shrink-0 text-gray-800 group-data-[state=checked]/DropdownMenuRadioItem:flex group-data-[state=unchecked]/DropdownMenuRadioItem:hidden dark:text-gray-200"
          />
        </span>
      ) : null}
      {children}
      {hint && (
        <span
          className={cx(
            'ml-auto text-sm font-normal text-gray-400 dark:text-gray-600',
          )}
        >
          {hint}
        </span>
      )}
      {shortcut && (
        <span
          className={cx(
            'ml-auto text-sm font-normal tracking-widest text-gray-400 dark:border-gray-800 dark:text-gray-600',
          )}
        >
          {shortcut}
        </span>
      )}
    </DropdownMenuPrimitives.RadioItem>
  ),
);

DropdownMenuRadioItem.displayName = 'DropdownMenuRadioItem';

export { DropdownMenuRadioItem };
